import { Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useProjectActivity } from "@/hooks/use-project-activity";

interface ActivityCardProps {
  projectId: string;
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "Yesterday";
  if (day < 7) return `${day} days ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function ActivityCard({ projectId }: ActivityCardProps) {
  const { data, isLoading } = useProjectActivity(projectId, 7);

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[15px] font-semibold">Recent activity</h3>
      </div>

      {isLoading ? (
        <div className="mt-3 space-y-3">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-10 w-full" />
          ))}
        </div>
      ) : !data || data.data.length === 0 ? (
        <p className="mt-3 text-[12px] text-muted-foreground">No recent activity yet.</p>
      ) : (
        <ol className="relative mt-3">
          <span className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
          {data.data.slice(0, 7).map((a) => (
            <li key={a.id} className="relative pl-7 py-2">
              <span className="absolute left-0 top-[10px] h-[15px] w-[15px] rounded-full bg-card border border-border inline-flex items-center justify-center">
                <Activity className="h-2.5 w-2.5 text-muted-foreground" />
              </span>
              <div className="text-[13px] leading-snug">
                <span className="font-medium">{a.who}</span>{" "}
                <span className="text-muted-foreground">{a.what}</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {relativeTime(a.createdAt)} · <span className="text-primary">{a.where}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
